/**
 * category-cascade.ts — event payload schemas for the single-category
 * "generate all content" cascade (idea 2026-07-17-category-cascade-generation).
 *
 * The cascade generates every content property of ONE category on ONE language,
 * in dependency order (keywords first, then the fields that consume them, then
 * the fields that consume those). Each dependency level is one LLM call whose
 * output is persisted before the next level runs, so downstream fields ground on
 * the REAL, human-inspectable values.
 */
import { z } from 'zod';
import { costValueSchema } from '../../pricing/cost-value.js';

/** admin → worker: start a cascade for one (category, language). */
export const categoryCascadeRequestedSchema = z.object({
  category_id: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  /** Regenerate fields even when already populated. Default false = empty-only. */
  overwrite: z.boolean().default(false),
});
export type CategoryCascadeRequestedPayload = z.infer<typeof categoryCascadeRequestedSchema>;

/** worker → admin: cascade accepted, announces the level plan. */
export const categoryCascadeStartedSchema = z.object({
  category_id: z.number().int().positive(),
  target_lang: z.string(),
  /** Ordered dependency levels; each level is the list of fields it generates. */
  levels: z.array(z.array(z.string())),
  total_levels: z.number().int().nonnegative(),
});

/** worker → admin: one dependency level finished (or was skipped). */
export const categoryCascadeLevelDoneSchema = z.object({
  category_id: z.number().int().positive(),
  target_lang: z.string(),
  level_index: z.number().int().nonnegative(),
  level_fields: z.array(z.string()),
  /** Fields actually generated + persisted this level (⊆ level_fields). */
  generated_fields: z.array(z.string()),
  tokens_in: z.number().nonnegative(),
  tokens_out: z.number().nonnegative(),
  cost: costValueSchema,
});

/**
 * worker → admin: a level failed — always loud.
 *
 * `fatal` distinguishes the two failure classes (idea 2026-07-17, wave-4
 * finding): `true` (default, back-compat) aborts the cascade — transport/persist
 * failures where continuing just burns money. `false` is a DEGRADED level: the
 * response could not be read for `failed_fields`, those fields stay missing, and
 * the cascade keeps walking the remaining levels.
 */
export const categoryCascadeFailedSchema = z.object({
  category_id: z.number().int().positive(),
  target_lang: z.string(),
  level_index: z.number().int().nonnegative(),
  level_fields: z.array(z.string()),
  /** Which fields of this level did not materialise (⊆ level_fields). */
  failed_fields: z.array(z.string()).optional(),
  /** false ⇒ the cascade continued past this level. Absent ⇒ fatal (legacy). */
  fatal: z.boolean().optional(),
  error: z.string(),
});

/** worker → admin: whole cascade finished successfully. */
export const categoryCascadeDoneSchema = z.object({
  category_id: z.number().int().positive(),
  target_lang: z.string(),
  done_levels: z.number().int().nonnegative(),
  total_levels: z.number().int().nonnegative(),
  generated_fields: z.array(z.string()),
  /** Fields that degraded levels could not produce — empty on a clean run. */
  failed_fields: z.array(z.string()).optional(),
  tokens_in: z.number().nonnegative(),
  tokens_out: z.number().nonnegative(),
  cost: costValueSchema,
  duration_ms: z.number().nonnegative(),
});
